import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MapPin, MapTrifold, Plus, Trash, X, ArrowSquareOut, Plugs, PencilSimple, ClipboardText, Rows,
  MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsIn, Lightning,
  Laptop, Monitor, DeviceMobile, Printer, WifiHigh, HardDrives, Database, Keyboard, Chair,
  User, SquaresFour, Printer as PrinterIcon,
} from '@phosphor-icons/react';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import WalkPanel from './ITInventoryWalk';
import RackDialog from './ITRackView';
import AssetFormDialog from './AssetFormDialog';
import LinkOmadaDialog, { shouldSkipAutoMatch } from './LinkOmadaDialog';
import UnmatchedOmadaList from './UnmatchedOmadaList';
import { itApi, fileUrl } from './itApi';
import { categoryColor } from './categoryColor';
import { suggestMatches } from './assetMatcher';
import { formatDate } from '@/utils/datetime';

// ピンアイコン — IT機器に合わせたシンボル。'auto' はカテゴリから自動判定。
const PIN_ICONS = {
  pin: { Icon: MapPin, label: 'ピン（標準）' },
  pc: { Icon: Laptop, label: 'PC・ノートPC' },
  monitor: { Icon: Monitor, label: 'モニター' },
  phone: { Icon: DeviceMobile, label: 'スマホ・タブレット' },
  printer: { Icon: Printer, label: 'プリンター・複合機' },
  network: { Icon: WifiHigh, label: 'ネットワーク機器' },
  server: { Icon: HardDrives, label: 'サーバー' },
  storage: { Icon: Database, label: 'NAS・ストレージ' },
  peripheral: { Icon: Keyboard, label: '周辺機器' },
  furniture: { Icon: Chair, label: '家具・什器' },
};

const NETWORK_KEYS = new Set(['network', 'server', 'storage']);

// ケーブル種別 — 図面凡例（光ファイバー: 赤一点鎖線 / LAN CAT5/6/6E / 無線Wi-Fi）に準拠
const CABLE_TYPES = {
  fiber: { label: '光ファイバー', color: '#e11d48', dash: [10, 5, 2, 5], short: '光' },
  cat5:  { label: 'LAN CAT5',    color: '#f97316', dash: [4, 3],         short: 'CAT5' },
  cat6:  { label: 'LAN CAT6',    color: '#ca8a04', dash: [8, 5],         short: 'CAT6' },
  cat6e: { label: 'LAN CAT6E',   color: '#059669', dash: [10, 4],        short: 'CAT6E' },
  wifi:  { label: '無線 Wi-Fi',   color: '#0284c7', dash: [2, 4],         short: '無線' },
  other: { label: 'その他',       color: '#71717a', dash: [],             short: '他' },
  // 後方互換: 既存 'lan' 種別は CAT6 と同等扱い（新規は cat6 を利用）
  lan:   { label: 'LAN CAT6',    color: '#ca8a04', dash: [8, 5],         short: 'CAT6', legacy: true },
};
// 新規接続時に選べる種別（legacy 'lan' は隠す）
const CABLE_TYPE_KEYS = ['fiber', 'cat5', 'cat6', 'cat6e', 'wifi', 'other'];

const VIEW_MODES = [
  ['all', 'すべて'],
  ['position', '配置ビュー'],
  ['network', 'ネットワークビュー'],
];

const categoryToIcon = (category) => {
  const c = category || '';
  if (/PC|パソコン|ノート/.test(c)) return 'pc';
  if (/モニター|ディスプレイ/.test(c)) return 'monitor';
  if (/スマート|タブレット|携帯|電話/.test(c)) return 'phone';
  if (/プリンター|複合機/.test(c)) return 'printer';
  if (/ネットワーク|ルーター|Wi-?Fi|スイッチ/i.test(c)) return 'network';
  if (/サーバー/.test(c)) return 'server';
  if (/NAS|ストレージ/.test(c)) return 'storage';
  if (/周辺機器|キーボード|マウス/.test(c)) return 'peripheral';
  if (/家具|什器/.test(c)) return 'furniture';
  return 'pin';
};

const iconKeyFor = (asset) => (asset.pin_icon && PIN_ICONS[asset.pin_icon] ? asset.pin_icon : categoryToIcon(asset.category));

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

// 資産グループ化用のキー（席/グループ名 > 使用者 > 共有部門の優先順でまとめる）
const seatKeyOf = (label) => `seat:${label.replace(/[.$]/g, '')}`;
const groupKeyFor = (a) => {
  if (a.pin_group) return seatKeyOf(a.pin_group);
  if (a.assigned_to_employee_id) return `emp:${a.assigned_to_employee_id}`;
  if (a.assigned_to_department_id && !a.assigned_to_employee_id) return `dep:${a.assigned_to_department_id}`;
  return null;
};
const groupLabelFor = (a) => {
  if (a.pin_group) return a.pin_group;
  if (a.assigned_to_employee_id) return a.assigned_to_name || '担当者';
  if (a.assigned_to_department_id) return `${a.assigned_to_department_name || '部門'}（共有）`;
  return '';
};
const GROUPS_ON_KEY = 'it-floormap-groups-on';

export default function ITFloormap() {
  const { user } = useAuth();
  const canEdit = canEditIt(user);
  const navigate = useNavigate();
  const [maps, setMaps] = useState([]);
  const [assets, setAssets] = useState([]);
  const [conns, setConns] = useState([]);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [placingAssetId, setPlacingAssetId] = useState('');
  const [placingIcon, setPlacingIcon] = useState('auto');
  const [connecting, setConnecting] = useState(null); // {fromId, cableType}
  const [viewMode, setViewMode] = useState('all');    // 'all' | 'position' | 'network'
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [popoverId, setPopoverId] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [hoverConn, setHoverConn] = useState(null);   // ホバー中の接続 id
  const [connectLabel, setConnectLabel] = useState(''); // 接続作成時のメモ（ポート/VLAN）
  const [editingConn, setEditingConn] = useState(null); // {id, label} メモ編集中
  const [walk, setWalk] = useState(null);             // 進行中の棚卸ウォーク
  const [walkResult, setWalkResult] = useState(null); // 完了レポート
  const [walkResultOpen, setWalkResultOpen] = useState(false);
  // ラック
  const [racks, setRacks] = useState([]);
  const [placingRack, setPlacingRack] = useState(false);
  const [newRack, setNewRack] = useState(null);       // {x, y, name, units} 作成ダイアログ
  const [rackDialogId, setRackDialogId] = useState(null);
  const [rackPicker, setRackPicker] = useState(null); // 接続先ラックの機器選択
  const [dragRack, setDragRack] = useState(null);     // {id, x, y}
  const [dragPin, setDragPin] = useState(null);       // {id, x, y} ドラッグ中の座標
  // ズーム・パン (大きな図面向け)
  const [viewT, setViewT] = useState({ zoom: 1, x: 0, y: 0 });
  const viewRef = useRef(viewT);
  viewRef.current = viewT;
  // グループ表示（席・使用者ごとの囲み枠）— デフォルトON、ユーザーごとに永続化
  const [groupsOn, setGroupsOn] = useState(() => {
    try { const v = localStorage.getItem(GROUPS_ON_KEY); return v == null ? true : v === '1'; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem(GROUPS_ON_KEY, groupsOn ? '1' : '0'); } catch { /* noop */ } }, [groupsOn]);
  // グループカードのドラッグ/リサイズ・実寸計測
  const [dragGroup, setDragGroup] = useState(null); // {key, x, y, w?, h?}
  const [cardDims, setCardDims] = useState({});     // key -> {w, h} px (自動サイズ計測)
  const cardEls = useRef(new Map());
  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const pinDragRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, rows] = await Promise.all([itApi.floormaps(), itApi.assets({})]);
      setMaps(m);
      setAssets(rows);
      setSelectedMapId((prev) => prev && m.some((x) => x.id === prev) ? prev : (m[0]?.id || null));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadConns = useCallback(async () => {
    if (!selectedMapId) { setConns([]); return; }
    try { setConns(await itApi.floormapConnections(selectedMapId)); } catch { setConns([]); }
  }, [selectedMapId]);
  useEffect(() => { loadConns(); }, [loadConns]);

  const loadRacks = useCallback(async () => {
    if (!selectedMapId) { setRacks([]); return; }
    try { setRacks(await itApi.racks(selectedMapId)); } catch { setRacks([]); }
  }, [selectedMapId]);
  useEffect(() => { loadRacks(); }, [loadRacks]);

  // Omada 未登録機器（資産に紐付いていない機器）
  const [unmatched, setUnmatched] = useState(null);
  const [quickReg, setQuickReg] = useState(null); // {prefill, prefillIp}
  const [linkTarget, setLinkTarget] = useState(null); // {row, mode:'link'|'confirm-before-register'}
  const [assetsCache, setAssetsCache] = useState([]); // Auto-Match on Register 用の資産一覧キャッシュ
  const loadUnmatched = useCallback(() => {
    if (!canEdit) return;
    itApi.omadaUnmatched().then(setUnmatched).catch(() => setUnmatched(null));
  }, [canEdit]);
  useEffect(() => { loadUnmatched(); }, [loadUnmatched]);
  // 資産一覧を1回だけ取得（+登録 タップ時の類似判定に使う — 毎回API呼ばず即応）
  useEffect(() => { if (canEdit) itApi.assets().then(setAssetsCache).catch(() => setAssetsCache([])); }, [canEdit]);

  const buildQuickReg = (row, isDevice) => ({
    prefill: {
      name: row.name || row.model || row.mac,
      category: isDevice ? 'ネットワーク機器' : 'PC・ノートPC',
      status: 'in_use',
      notes: `Omada検出 MAC: ${row.mac}${row.ip ? ` / IP: ${row.ip}` : ''}`,
    },
    prefillIp: row.ip_entry_id ? {
      vlan_id: row.vlan_id, ip_entry_id: row.ip_entry_id,
      ip_address: row.entry_ip || row.ip,
    } : null,
  });

  const openQuickReg = (row, isDevice) => {
    // Auto-Match: 類似する既存資産があれば先に紐付け候補を提示する（設定でスキップ可）
    if (!shouldSkipAutoMatch()) {
      const name = row.name || row.model;
      const matches = name ? suggestMatches(name, assetsCache, { limit: 4, threshold: 0.7 }) : [];
      if (matches.length > 0) {
        setLinkTarget({ ...row, __fallbackRegister: { row, isDevice } });
        return;
      }
    }
    setQuickReg(buildQuickReg(row, isDevice));
  };

  // マップ切替時にビューをリセット + 進行中の棚卸があれば再開
  useEffect(() => {
    setViewT({ zoom: 1, x: 0, y: 0 }); setPopoverId(null); setConnecting(null); setDragPin(null);
    setWalkResult(null); setWalkResultOpen(false); setHoverConn(null); setEditingConn(null);
    if (!selectedMapId) { setWalk(null); return; }
    itApi.walks(selectedMapId, 'active').then((ws) => setWalk(ws[0] || null)).catch(() => setWalk(null));
  }, [selectedMapId]);

  // 画像サイズ追跡（配線パスのピクセル座標計算用）
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const update = () => setImgSize({ w: img.offsetWidth, h: img.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(img);
    return () => ro.disconnect();
  }, [selectedMapId, maps.length]);

  // ホイールでズーム（カーソル位置基準）
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const { zoom, x, y } = viewRef.current;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      if (nz === zoom) return;
      if (nz === 1) { setViewT({ zoom: 1, x: 0, y: 0 }); return; }
      const k = nz / zoom;
      setViewT({ zoom: nz, x: cx - (cx - x) * k, y: cy - (cy - y) * k });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectedMapId, maps.length]);

  const zoomBy = (factor) => {
    const el = viewportRef.current;
    const { zoom, x, y } = viewRef.current;
    const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * factor));
    if (nz === zoom) return;
    if (nz === 1) { setViewT({ zoom: 1, x: 0, y: 0 }); return; }
    const rect = el ? el.getBoundingClientRect() : { width: 0, height: 0 };
    const k = nz / zoom;
    setViewT({ zoom: nz, x: rect.width / 2 - (rect.width / 2 - x) * k, y: rect.height / 2 - (rect.height / 2 - y) * k });
  };
  const resetView = () => setViewT({ zoom: 1, x: 0, y: 0 });

  // ドラッグでパン（ズーム中のみ）
  const onPointerDown = (e) => {
    if (viewRef.current.zoom <= 1) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: viewRef.current.x, oy: viewRef.current.y, moved: false };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    if (d.moved) setViewT((v) => ({ ...v, x: d.ox + dx, y: d.oy + dy }));
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.moved) {
      dragRef.suppressClick = true;
      setTimeout(() => { dragRef.suppressClick = false; }, 0);
    }
  };

  const selectedMap = maps.find((m) => m.id === selectedMapId) || null;
  const pinned = useMemo(
    () => assets.filter((a) => a.floormap_id === selectedMapId && a.pin_x != null),
    [assets, selectedMapId],
  );
  const pinById = useMemo(() => new Map(pinned.map((a) => [a.id, a])), [pinned]);
  const rackById = useMemo(() => new Map(racks.map((r) => [r.id, r])), [racks]);
  const rackedAssets = useMemo(
    () => assets.filter((a) => a.rack_id && rackById.has(a.rack_id)),
    [assets, rackById],
  );
  // ラックごとの使用U数（前面/背面いずれかで占有されているUスロットの合計）
  const rackUsage = useMemo(() => {
    const map = new Map();
    rackedAssets.forEach((a) => {
      if (!a.rack_id) return;
      let occ = map.get(a.rack_id);
      if (!occ) { occ = new Set(); map.set(a.rack_id, occ); }
      const start = a.rack_unit || 1;
      for (let i = 0; i < (a.rack_size || 1); i += 1) occ.add(start + i);
    });
    return map;
  }, [rackedAssets]);
  const usageFor = (r) => {
    const used = rackUsage.get(r.id)?.size || 0;
    const units = r.units || 42;
    return { used, free: Math.max(0, units - used), ratio: units ? used / units : 0 };
  };
  const usageCls = (ratio) => (ratio >= 0.9 ? 'bg-rose-600' : ratio >= 0.7 ? 'bg-amber-500' : 'bg-emerald-600');
  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);
  // 接続線の端点座標 — 単独ピン or 搭載先ラックの位置
  const posById = useMemo(() => {
    const m = new Map();
    pinned.forEach((a) => m.set(a.id, { x: a.pin_x, y: a.pin_y }));
    rackedAssets.forEach((a) => {
      const r = rackById.get(a.rack_id);
      const rp = dragRack?.id === r.id ? dragRack : { x: r.pin_x, y: r.pin_y };
      m.set(a.id, { x: rp.x, y: rp.y });
    });
    if (dragPin && m.has(dragPin.id)) m.set(dragPin.id, { x: dragPin.x, y: dragPin.y });
    return m;
  }, [pinned, rackedAssets, rackById, dragPin, dragRack]);
  const popoverAsset = popoverId ? pinById.get(popoverId) : null;
  // 席/グループ名の編集ドラフト（ポップオーバー内）
  const [groupNameDraft, setGroupNameDraft] = useState('');
  useEffect(() => { setGroupNameDraft(popoverAsset?.pin_group || ''); }, [popoverId]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePinGroup = async () => {
    if (!popoverAsset) return;
    const name = groupNameDraft.trim() || null;
    try {
      await itApi.setAssetPinGroup(popoverAsset.id, name);
      toast.success(name ? `グループ名「${name}」を設定しました` : 'グループ名を解除しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'グループ名の保存に失敗しました');
    }
  };
  const visibleConns = useMemo(
    () => conns.filter((c) => posById.has(c.from_asset_id) && posById.has(c.to_asset_id)),
    [conns, posById],
  );

  // ドラッグ中はローカル座標を優先
  const coordsFor = (a) => (dragPin && dragPin.id === a.id ? { x: dragPin.x, y: dragPin.y } : { x: a.pin_x, y: a.pin_y });

  // EdrawMax 風の直角配線（マンハッタンルーティング）— 端点はカードポート > effPosById（ピン/ラック/カード）
  const cablePath = (c) => {
    const { w, h } = imgSize;
    const fc = groupPorts.byConn.get(`${c.id}:from`) || effPosById.get(c.from_asset_id);
    const tc = groupPorts.byConn.get(`${c.id}:to`) || effPosById.get(c.to_asset_id);
    const x1 = (fc.x / 100) * w; const y1 = (fc.y / 100) * h;
    const x2 = (tc.x / 100) * w; const y2 = (tc.y / 100) * h;
    const midY = (y1 + y2) / 2;
    return `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
  };

  const createMap = async () => {
    if (!newName.trim() || !newFile) { toast.error('名前と画像を指定してください'); return; }
    setBusy(true);
    try {
      await itApi.createFloormap(newName.trim(), newFile);
      toast.success('フロアマップを追加しました');
      setNewOpen(false); setNewName(''); setNewFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '追加に失敗しました');
    } finally { setBusy(false); }
  };

  const deleteMap = async () => {
    if (!selectedMap) return;
    if (!window.confirm(`フロアマップ「${selectedMap.name}」を削除しますか？配置済みのピンと接続も解除されます。`)) return;
    try {
      await itApi.deleteFloormap(selectedMap.id);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const onMapClick = async (e) => {
    if (dragRef.suppressClick || pinDragRef.suppress) return;
    if (popoverId) { setPopoverId(null); return; }
    if (connecting) { setConnecting(null); toast.info('接続を中止しました'); return; }
    if (placingRack && canEdit && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      setNewRack({
        x: Math.round(((e.clientX - rect.left) / rect.width) * 1000) / 10,
        y: Math.round(((e.clientY - rect.top) / rect.height) * 1000) / 10,
        name: '', units: '42',
      });
      setPlacingRack(false);
      return;
    }
    if (!canEdit || !placingAssetId || !imgRef.current || !selectedMapId) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    try {
      await itApi.setAssetPin(placingAssetId, {
        floormap_id: selectedMapId,
        pin_x: Math.round(x * 10) / 10,
        pin_y: Math.round(y * 10) / 10,
        pin_icon: placingIcon === 'auto' ? null : placingIcon,
      });
      toast.success('ピンを配置しました');
      setPlacingAssetId('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'ピン配置に失敗しました');
    }
  };

  const removePin = async (assetId) => {
    try {
      await itApi.setAssetPin(assetId, { floormap_id: null });
      toast.success('ピンを解除しました');
      setPopoverId(null);
      load(); loadConns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ピン解除に失敗しました');
    }
  };

  const changeIcon = async (assetId, iconKey) => {
    try {
      await itApi.setAssetPinIcon(assetId, iconKey);
      toast.success('アイコンを変更しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アイコン変更に失敗しました');
    }
  };

  const startConnect = (fromId, cableType) => {
    setConnecting({ fromId, cableType });
    setPopoverId(null);
  };

  const createConnection = async (toId) => {
    if (!connecting || !selectedMapId) return;
    if (toId === connecting.fromId) { toast.error('同じ資産同士は接続できません'); return; }
    try {
      await itApi.createFloormapConnection(selectedMapId, {
        from_asset_id: connecting.fromId, to_asset_id: toId, cable_type: connecting.cableType,
        label: connectLabel.trim() || null,
      });
      toast.success('ケーブル接続を追加しました');
      setConnecting(null);
      setConnectLabel('');
      loadConns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '接続の追加に失敗しました');
    }
  };

  const deleteConnection = async (cid) => {
    try {
      await itApi.deleteFloormapConnection(selectedMapId, cid);
      toast.success('接続を削除しました');
      loadConns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '接続の削除に失敗しました');
    }
  };

  const saveConnLabel = async () => {
    if (!editingConn) return;
    try {
      await itApi.updateFloormapConnection(selectedMapId, editingConn.id, { label: editingConn.label });
      toast.success('メモを保存しました');
      setEditingConn(null);
      loadConns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'メモの保存に失敗しました');
    }
  };

  // 棚卸ウォーク
  const startWalk = async () => {
    try {
      const w = await itApi.startWalk(selectedMapId);
      setWalk(w);
      setWalkResult(null);
      toast.success((w.scans || []).length ? '進行中の棚卸を再開しました' : '棚卸を開始しました — QRラベルをスキャンしてください');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '棚卸の開始に失敗しました');
    }
  };

  const onWalkFinished = (result) => {
    setWalk(null);
    setWalkResult(result);
    setWalkResultOpen(true);
    const missing = result?.summary?.missing_asset_ids?.length || 0;
    if (missing) toast.warning(`棚卸完了 — 未確認の資産が ${missing} 件あります`);
    else toast.success('棚卸完了 — すべての資産を確認しました');
  };

  const walkStatusById = useMemo(() => {
    const m = new Map();
    (walk?.scans || []).forEach((s) => { if (s.asset_id) m.set(s.asset_id, s.status); });
    (walkResult?.summary?.missing_asset_ids || []).forEach((id) => m.set(id, 'missing'));
    (walkResult?.scans || []).forEach((s) => { if (s.asset_id && s.status === 'ok') m.set(s.asset_id, 'ok'); });
    return m;
  }, [walk, walkResult]);

  // ピンのドラッグ移動（EdrawMax 風の直接操作）
  const onPinMouseDown = (e, a) => {
    if (!canEdit || connecting) return;
    e.stopPropagation();
    e.preventDefault();
    pinDragRef.current = { id: a.id, sx: e.clientX, sy: e.clientY, moved: false, icon: a.pin_icon || null };
    const clampPct = (v) => Math.min(100, Math.max(0, Math.round(v * 10) / 10));
    const onMove = (ev) => {
      const d = pinDragRef.current;
      if (!d || !imgRef.current) return;
      if (Math.abs(ev.clientX - d.sx) > 4 || Math.abs(ev.clientY - d.sy) > 4) d.moved = true;
      if (!d.moved) return;
      const rect = imgRef.current.getBoundingClientRect();
      setDragPin({
        id: d.id,
        x: clampPct(((ev.clientX - rect.left) / rect.width) * 100),
        y: clampPct(((ev.clientY - rect.top) / rect.height) * 100),
      });
    };
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const d = pinDragRef.current;
      pinDragRef.current = null;
      if (!d?.moved || !imgRef.current) return;
      pinDragRef.suppress = true;
      setTimeout(() => { pinDragRef.suppress = false; }, 50);
      const rect = imgRef.current.getBoundingClientRect();
      const x = clampPct(((ev.clientX - rect.left) / rect.width) * 100);
      const y = clampPct(((ev.clientY - rect.top) / rect.height) * 100);
      try {
        await itApi.setAssetPin(d.id, { floormap_id: selectedMapId, pin_x: x, pin_y: y, pin_icon: d.icon });
        toast.success('ピンを移動しました');
        await load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'ピン移動に失敗しました');
      }
      setDragPin(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const onPinClick = (e, a) => {
    e.stopPropagation();
    if (dragRef.suppressClick || pinDragRef.suppress) return;
    if (connecting) { createConnection(a.id); return; }
    if (canEdit) {
      setPopoverId((prev) => (prev === a.id ? null : a.id));
    } else {
      navigate(`/it/assets?open=${a.id}`);
    }
  };

  const { zoom } = viewT;
  const invScale = 1 / zoom;
  const showCables = viewMode !== 'position';

  // 資産グループ（席/使用者ごとの資産カード）— 2件以上の資産で構成される場合のみ描画
  const groups = useMemo(() => {
    if (!groupsOn) return [];
    const layout = selectedMap?.group_layout || {};
    const bag = new Map(); // key -> { key, label, members: [], xs: [], ys: [] }
    const push = (a, x, y) => {
      const key = groupKeyFor(a);
      if (!key || x == null) return;
      let g = bag.get(key);
      if (!g) { g = { key, label: groupLabelFor(a), members: [], xs: [], ys: [] }; bag.set(key, g); }
      g.members.push(a); g.xs.push(x); g.ys.push(y);
    };
    pinned.forEach((a) => push(a, a.pin_x, a.pin_y));
    // ラック搭載機器も同じ使用者に紐付いていればグループ化に加える
    rackedAssets.forEach((a) => {
      const r = rackById.get(a.rack_id);
      push(a, r?.pin_x, r?.pin_y);
    });
    const list = [];
    bag.forEach((g) => {
      if (g.members.length < 2) return;
      const saved = layout[g.key];
      list.push({
        ...g,
        x: saved?.x ?? Math.max(0, Math.min(...g.xs) - 2),
        y: saved?.y ?? Math.max(0, Math.min(...g.ys) - 2),
        w: saved?.w ?? null, h: saved?.h ?? null,
      });
    });
    return list;
  }, [groupsOn, pinned, rackedAssets, rackById, selectedMap]);

  // カードの自動サイズを実測（保存サイズが無い場合の表示・配線アンカーに使用）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!groupsOn) return;
    const next = {};
    cardEls.current.forEach((el, key) => { if (el) next[key] = { w: el.offsetWidth, h: el.offsetHeight }; });
    setCardDims((prev) => {
      const keys = Object.keys(next);
      if (keys.length === Object.keys(prev).length
        && keys.every((k) => prev[k] && prev[k].w === next[k].w && prev[k].h === next[k].h)) return prev;
      return next;
    });
  });

  // カードの現在の矩形（%座標）— ドラッグ/リサイズ中はローカル値を優先
  const groupRects = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => {
      const d = dragGroup?.key === g.key ? dragGroup : g;
      const dims = cardDims[g.key];
      const wSet = d.w != null; const hSet = d.h != null;
      const w = wSet ? d.w : (dims && imgSize.w ? (dims.w / imgSize.w) * 100 : 10);
      const h = hSet ? d.h : (dims && imgSize.h ? (dims.h / imgSize.h) * 100 : 6);
      m.set(g.key, { x: d.x, y: d.y, w, h, wSet, hSet });
    });
    return m;
  }, [groups, dragGroup, cardDims, imgSize]);

  const groupKeyByAsset = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => g.members.forEach((a) => m.set(a.id, g.key)));
    return m;
  }, [groups]);

  // 配線・ポップオーバー用の実効座標 — グループ所属資産はカード中心にアンカー
  const effPosById = useMemo(() => {
    const m = new Map(posById);
    groupKeyByAsset.forEach((key, aid) => {
      const r = groupRects.get(key);
      if (r) m.set(aid, { x: r.x + r.w / 2, y: r.y + r.h / 2 });
    });
    return m;
  }, [posById, groupKeyByAsset, groupRects]);

  // カードのケーブルポート — 接続ごとにカード辺上へ小さな色付きドットを配置
  const groupPorts = useMemo(() => {
    const byConn = new Map();  // `${connId}:from|to` -> {x, y}
    const byGroup = new Map(); // groupKey -> [{id, end, x, y, cableType}]
    if (!groupsOn || groups.length === 0) return { byConn, byGroup };
    groups.forEach((g) => {
      const r = groupRects.get(g.key);
      if (!r) return;
      const memberIds = new Set(g.members.map((a) => a.id));
      const cx = r.x + r.w / 2; const cy = r.y + r.h / 2;
      const items = [];
      conns.forEach((c) => {
        const fromIn = memberIds.has(c.from_asset_id);
        const toIn = memberIds.has(c.to_asset_id);
        if (fromIn === toIn) return; // カード内部配線・無関係は対象外
        const otherId = fromIn ? c.to_asset_id : c.from_asset_id;
        const op = effPosById.get(otherId);
        if (!op) return;
        // 相手方向から接続辺を判定（カードの縦横比で正規化）
        const dx = (op.x - cx) / Math.max(r.w / 2, 0.001);
        const dy = (op.y - cy) / Math.max(r.h / 2, 0.001);
        const side = Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? 'right' : 'left') : (dy >= 0 ? 'bottom' : 'top');
        items.push({ c, end: fromIn ? 'from' : 'to', side, order: (side === 'left' || side === 'right') ? op.y : op.x });
      });
      const bySide = { top: [], bottom: [], left: [], right: [] };
      items.forEach((it) => bySide[it.side].push(it));
      const ports = [];
      Object.entries(bySide).forEach(([side, arr]) => {
        arr.sort((a, b) => a.order - b.order); // 相手位置順で交差を減らす
        arr.forEach((it, i) => {
          const f = (i + 1) / (arr.length + 1);
          const x = side === 'left' ? r.x : side === 'right' ? r.x + r.w : r.x + r.w * f;
          const y = side === 'top' ? r.y : side === 'bottom' ? r.y + r.h : r.y + r.h * f;
          byConn.set(`${it.c.id}:${it.end}`, { x, y });
          ports.push({ id: it.c.id, end: it.end, x, y, cableType: it.c.cable_type });
        });
      });
      byGroup.set(g.key, ports);
    });
    return { byConn, byGroup };
  }, [groupsOn, groups, groupRects, conns, effPosById]);

  // 印刷用PDF出力 — ズーム/パンを一時的にリセットし、ブラウザの印刷ダイアログ（"PDFに保存"）を起動
  const printFloormap = () => {
    if (!selectedMap) return;
    const saved = { view: viewT, popover: popoverId };
    try {
      setViewT({ zoom: 1, x: 0, y: 0 });
      setPopoverId(null);
      document.body.classList.add('floormap-printing');
      setTimeout(() => {
        try { window.print(); } finally {
          document.body.classList.remove('floormap-printing');
          setViewT(saved.view); setPopoverId(saved.popover);
        }
      }, 300);
    } catch (e) {
      document.body.classList.remove('floormap-printing');
      setViewT(saved.view); setPopoverId(saved.popover);
      toast.error('印刷準備に失敗しました');
    }
  };

  // 全マップ一括印刷 — 表紙 + 各マップを順番に印刷
  const [printAllBusy, setPrintAllBusy] = useState(false);
  const [printCoverMode, setPrintCoverMode] = useState(false);
  const [coverStats, setCoverStats] = useState(null);

  const printCoverPage = () => new Promise((resolve) => {
    setPrintCoverMode(true);
    setTimeout(() => {
      const saved = { view: viewT, popover: popoverId };
      setViewT({ zoom: 1, x: 0, y: 0 });
      setPopoverId(null);
      document.body.classList.add('floormap-printing');
      document.body.classList.add('floormap-printing-cover');
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        window.removeEventListener('afterprint', finish);
        document.body.classList.remove('floormap-printing');
        document.body.classList.remove('floormap-printing-cover');
        setPrintCoverMode(false);
        setViewT(saved.view); setPopoverId(saved.popover);
        resolve();
      };
      window.addEventListener('afterprint', finish);
      setTimeout(() => { try { window.print(); } catch { finish(); } }, 300);
      setTimeout(finish, 60000);
    }, 100);
  });

  const printAllFloormaps = async () => {
    if (maps.length === 0) return;
    if (maps.length === 1) return printFloormap();
    if (!window.confirm(`表紙1枚＋${maps.length}枚のフロアマップを順番に印刷します。各マップごとに保存ダイアログが開きます。よろしいですか？`)) return;
    setPrintAllBusy(true);
    const originalId = selectedMapId;
    try {
      // 表紙用の集計 — 全マップ横断でラック・接続数を合算
      const perMap = await Promise.all(maps.map(async (m) => {
        try {
          const [mConns, mRacks] = await Promise.all([itApi.floormapConnections(m.id), itApi.racks(m.id)]);
          const pinnedCount = assets.filter((a) => a.floormap_id === m.id && a.pin_x != null).length;
          const rackedCount = assets.filter((a) => a.floormap_id === m.id && a.rack_id).length;
          return { map: m, conns: mConns.length, racks: mRacks.length, pinned: pinnedCount, racked: rackedCount };
        } catch {
          return { map: m, conns: 0, racks: 0, pinned: 0, racked: 0 };
        }
      }));
      const stats = {
        maps: maps.length,
        assets: perMap.reduce((s, x) => s + x.pinned + x.racked, 0),
        conns: perMap.reduce((s, x) => s + x.conns, 0),
        racks: perMap.reduce((s, x) => s + x.racks, 0),
        perMap,
        date: formatDate(new Date()),
      };
      setCoverStats(stats);
      toast.message('表紙を印刷しています…');
      await printCoverPage();

      for (let i = 0; i < maps.length; i += 1) {
        const m = maps[i];
        setSelectedMapId(m.id);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1200));
        toast.message(`印刷中: ${m.name}（${i + 1}/${maps.length}）`);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          let done = false;
          const finish = () => { if (done) return; done = true; window.removeEventListener('afterprint', finish); resolve(); };
          window.addEventListener('afterprint', finish);
          printFloormap();
          setTimeout(finish, 60000);
        });
      }
      toast.success(`表紙＋${maps.length}枚のマップを印刷しました`);
    } finally {
      setCoverStats(null);
      setSelectedMapId(originalId);
      setPrintAllBusy(false);
    }
  };

  // 一括整列 — すべてのグループカードを左上から等間隔グリッドにスナップ
  const [tidyBusy, setTidyBusy] = useState(false);
  const tidyGroups = async () => {
    if (!selectedMapId || !imgSize.w || groups.length === 0) return;
    if (!window.confirm(`${groups.length}件のグループカードをグリッド整列します。よろしいですか？`)) return;
    setTidyBusy(true);
    try {
      const cols = Math.max(1, Math.ceil(Math.sqrt(groups.length)));
      const padX = 3, padY = 3, gapX = 2, gapY = 2;
      const cellW = (100 - padX * 2 - gapX * (cols - 1)) / cols;
      const rows = Math.ceil(groups.length / cols);
      const cellH = Math.min(14, (100 - padY * 2 - gapY * (rows - 1)) / rows);
      const clampPct = (v) => Math.min(100, Math.max(0, Math.round(v * 10) / 10));
      const sorted = [...groups].sort((a, b) => a.label.localeCompare(b.label, 'ja'));
      for (let i = 0; i < sorted.length; i += 1) {
        const g = sorted[i];
        const col = i % cols; const row = Math.floor(i / cols);
        const nx = clampPct(padX + col * (cellW + gapX));
        const ny = clampPct(padY + row * (cellH + gapY));
        const w = clampPct(cellW); const h = clampPct(cellH);
        const r0 = groupRects.get(g.key) || { x: g.x, y: g.y };
        const dx = nx - r0.x; const dy = ny - r0.y;
        // eslint-disable-next-line no-await-in-loop
        await itApi.updateFloormapGroupLayout(selectedMapId, { key: g.key, x: nx, y: ny, w, h });
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(g.members
          .filter((a) => a.floormap_id === selectedMapId && a.pin_x != null)
          .map((a) => itApi.setAssetPin(a.id, {
            floormap_id: selectedMapId,
            pin_x: clampPct(a.pin_x + dx), pin_y: clampPct(a.pin_y + dy),
            pin_icon: a.pin_icon || null,
          })));
      }
      toast.success(`${sorted.length}件のグループを整列しました`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'グループ整列に失敗しました');
    } finally { setTidyBusy(false); }
  };

  // グループカードのドラッグ移動 — メンバーのピン座標も同じ移動量で追従・保存
  const onGroupMouseDown = (e, g) => {
    if (!canEdit || connecting || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const r0 = groupRects.get(g.key);
    if (!r0) return;
    const st = { sx: e.clientX, sy: e.clientY, moved: false };
    const clampPct = (v) => Math.min(100, Math.max(0, Math.round(v * 10) / 10));
    const deltas = (ev) => {
      const rect = imgRef.current.getBoundingClientRect();
      return {
        dx: ((ev.clientX - st.sx) / rect.width) * 100,
        dy: ((ev.clientY - st.sy) / rect.height) * 100,
      };
    };
    const onMove = (ev) => {
      if (!imgRef.current) return;
      if (Math.abs(ev.clientX - st.sx) > 4 || Math.abs(ev.clientY - st.sy) > 4) st.moved = true;
      if (!st.moved) return;
      const { dx, dy } = deltas(ev);
      setDragGroup({
        key: g.key, x: clampPct(r0.x + dx), y: clampPct(r0.y + dy),
        w: r0.wSet ? r0.w : undefined, h: r0.hSet ? r0.h : undefined,
      });
    };
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!st.moved || !imgRef.current) { setDragGroup(null); return; }
      pinDragRef.suppress = true;
      setTimeout(() => { pinDragRef.suppress = false; }, 50);
      const { dx, dy } = deltas(ev);
      const nx = clampPct(r0.x + dx); const ny = clampPct(r0.y + dy);
      try {
        await itApi.updateFloormapGroupLayout(selectedMapId, {
          key: g.key, x: nx, y: ny,
          w: r0.wSet ? r0.w : null, h: r0.hSet ? r0.h : null,
        });
        await Promise.all(g.members
          .filter((a) => a.floormap_id === selectedMapId && a.pin_x != null)
          .map((a) => itApi.setAssetPin(a.id, {
            floormap_id: selectedMapId,
            pin_x: clampPct(a.pin_x + (nx - r0.x)),
            pin_y: clampPct(a.pin_y + (ny - r0.y)),
            pin_icon: a.pin_icon || null,
          })));
        toast.success('グループを移動しました');
        await load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'グループの移動に失敗しました');
      }
      setDragGroup(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // グループカードのリサイズ（右下ハンドル）— サイズは保存され次回も維持
  const onGroupResizeDown = (e, g) => {
    if (!canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const r0 = groupRects.get(g.key);
    if (!r0) return;
    const st = { sx: e.clientX, sy: e.clientY };
    const minW = imgSize.w ? (120 / imgSize.w) * 100 : 5;
    const minH = imgSize.h ? (48 / imgSize.h) * 100 : 4;
    const calc = (ev) => {
      const rect = imgRef.current.getBoundingClientRect();
      const dw = ((ev.clientX - st.sx) / rect.width) * 100;
      const dh = ((ev.clientY - st.sy) / rect.height) * 100;
      return {
        w: Math.min(100, Math.max(minW, Math.round((r0.w + dw) * 10) / 10)),
        h: Math.min(100, Math.max(minH, Math.round((r0.h + dh) * 10) / 10)),
      };
    };
    const onMove = (ev) => {
      if (!imgRef.current) return;
      setDragGroup({ key: g.key, x: r0.x, y: r0.y, ...calc(ev) });
    };
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      pinDragRef.suppress = true;
      setTimeout(() => { pinDragRef.suppress = false; }, 50);
      if (imgRef.current) {
        const { w, h } = calc(ev);
        try {
          await itApi.updateFloormapGroupLayout(selectedMapId, { key: g.key, x: r0.x, y: r0.y, w, h });
          toast.success('グループのサイズを変更しました');
          await load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'サイズ変更に失敗しました');
        }
      }
      setDragGroup(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // グループカード内の資産行クリック — 接続中は接続、通常はポップオーバー/詳細
  const onGroupRowClick = (e, a) => {
    e.stopPropagation();
    if (dragRef.suppressClick || pinDragRef.suppress) return;
    if (connecting) { createConnection(a.id); return; }
    if (canEdit && pinById.has(a.id)) {
      setPopoverId((prev) => (prev === a.id ? null : a.id));
    } else {
      navigate(`/it/assets?open=${a.id}`);
    }
  };

  // ラック操作
  const createRack = async () => {
    if (!newRack?.name?.trim()) { toast.error('ラック名を入力してください'); return; }
    try {
      await itApi.createRack(selectedMapId, {
        name: newRack.name.trim(), units: Math.max(1, Math.min(200, Number(newRack.units) || 42)),
        pin_x: newRack.x, pin_y: newRack.y,
      });
      toast.success('ラックを配置しました');
      setNewRack(null);
      loadRacks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ラックの作成に失敗しました');
    }
  };

  const onRackClick = (e, r) => {
    e.stopPropagation();
    if (dragRef.suppressClick || pinDragRef.suppress) return;
    if (connecting) {
      const devices = rackedAssets.filter((a) => a.rack_id === r.id);
      if (!devices.length) { toast.error('このラックに搭載機器がありません'); return; }
      if (devices.length === 1) { createConnection(devices[0].id); return; }
      setRackPicker(r);
      return;
    }
    setRackDialogId(r.id);
  };

  const onRackMouseDown = (e, r) => {
    if (!canEdit || connecting) return;
    e.stopPropagation();
    e.preventDefault();
    const st = { sx: e.clientX, sy: e.clientY, moved: false };
    const clampPct = (v) => Math.min(100, Math.max(0, Math.round(v * 10) / 10));
    const onMove = (ev) => {
      if (Math.abs(ev.clientX - st.sx) > 4 || Math.abs(ev.clientY - st.sy) > 4) st.moved = true;
      if (!st.moved || !imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      setDragRack({
        id: r.id,
        x: clampPct(((ev.clientX - rect.left) / rect.width) * 100),
        y: clampPct(((ev.clientY - rect.top) / rect.height) * 100),
      });
    };
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!st.moved || !imgRef.current) return;
      pinDragRef.suppress = true;
      setTimeout(() => { pinDragRef.suppress = false; }, 50);
      const rect = imgRef.current.getBoundingClientRect();
      try {
        await itApi.updateRack(r.id, {
          pin_x: clampPct(((ev.clientX - rect.left) / rect.width) * 100),
          pin_y: clampPct(((ev.clientY - rect.top) / rect.height) * 100),
        });
        toast.success('ラックを移動しました');
        await loadRacks();
      } catch { toast.error('ラック移動に失敗しました'); }
      setDragRack(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const assetConns = popoverAsset
    ? visibleConns.filter((c) => c.from_asset_id === popoverAsset.id || c.to_asset_id === popoverAsset.id)
    : [];

  return (
    <div className="space-y-4" data-testid="it-floormap-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">フロアマップ</h1>
          <p className="text-xs text-zinc-500 mt-0.5">IT資産の設置場所とケーブル配線（光ファイバー / LAN）を地図上で管理します。ピンはドラッグで移動、ホイール / ＋−ボタンで拡大。</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {selectedMap && !walk && (
              <Button variant="outline" onClick={startWalk} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="it-walk-start-btn">
                <ClipboardText size={15} /><span className="ml-1">棚卸モード</span>
              </Button>
            )}
            <Button onClick={() => setNewOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="it-floormap-new-btn">
              <Plus size={15} /><span className="ml-1">マップを追加</span>
            </Button>
          </div>
        )}
      </div>

      {maps.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/30 p-10 text-center">
          <MapTrifold size={32} weight="duotone" className="mx-auto text-teal-700 mb-2" />
          <div className="text-sm text-zinc-600">フロアマップがまだありません。<br />
            <span className="text-xs text-zinc-500">オフィスの見取り図（PNG/JPG）をアップロードして、IT資産の位置をピンで管理できます。</span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="it-floormap-tabs">
            {maps.map((m) => (
              <button key={m.id} type="button" onClick={() => setSelectedMapId(m.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  selectedMapId === m.id ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
                data-testid={`it-floormap-tab-${m.id}`}>
                {m.name}
              </button>
            ))}
            {canEdit && selectedMap && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-500 hover:bg-rose-50" onClick={deleteMap} data-testid="it-floormap-delete-btn">
                <Trash size={13} />
              </Button>
            )}
            {/* 表示レイヤー切替 */}
            <div className="ml-auto inline-flex items-center gap-2">
              <button type="button" onClick={() => setGroupsOn((v) => !v)}
                data-testid="it-groups-toggle"
                title="使用者ごとに囲み枠（席）を表示"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition-colors ${
                  groupsOn ? 'bg-teal-700/10 text-teal-700 border-teal-700/40' : 'bg-white text-zinc-500 border-zinc-200 hover:text-zinc-700'
                }`}>
                <span className={`inline-block w-2.5 h-2.5 rounded-sm border ${groupsOn ? 'border-teal-700 bg-teal-700/30' : 'border-zinc-400 bg-transparent'}`} />
                グループ表示
              </button>
              {canEdit && groupsOn && groups.length > 1 && (
                <button type="button" onClick={tidyGroups} disabled={tidyBusy}
                  data-testid="it-groups-tidy"
                  title="すべてのグループカードをグリッドに整列"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-emerald-300 bg-emerald-50 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors">
                  <SquaresFour size={12} weight="bold" />
                  {tidyBusy ? '整列中…' : '整列'}
                </button>
              )}
              <button type="button" onClick={printFloormap} disabled={printAllBusy}
                data-testid="it-floormap-print"
                title="現在のフロアマップを印刷用PDFとして出力"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-zinc-300 bg-white text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors">
                <PrinterIcon size={12} weight="bold" />
                印刷用PDF
              </button>
              {maps.length > 1 && (
                <button type="button" onClick={printAllFloormaps} disabled={printAllBusy}
                  data-testid="it-floormap-print-all"
                  title="すべてのフロアマップを順番に印刷（現場引継ぎパック用）"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-indigo-300 bg-indigo-50 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors">
                  <PrinterIcon size={12} weight="fill" />
                  {printAllBusy ? '印刷中…' : `全マップ一括印刷 (${maps.length})`}
                </button>
              )}
              <div className="inline-flex items-center gap-0.5 rounded-lg bg-zinc-100 p-0.5" data-testid="it-view-tabs">
                {VIEW_MODES.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setViewMode(k)}
                    data-testid={`it-view-${k}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                      viewMode === k ? 'bg-white text-teal-700 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2" data-testid="it-pin-toolbar">
              <MapPin size={15} weight="duotone" className="text-teal-700 shrink-0" />
              <span className="text-xs text-zinc-600 shrink-0">ピンを配置:</span>
              <div className="flex-1 min-w-52 max-w-sm">
                <SearchableSelect
                  value={placingAssetId}
                  onValueChange={setPlacingAssetId}
                  placeholder="資産を選択…"
                  searchPlaceholder="品名・資産番号で検索"
                  testid="it-pin-asset-select"
                  options={assets.map((a) => ({
                    value: a.id,
                    primary: a.name,
                    secondary: `${a.asset_tag || ''}${a.location ? ` / ${a.location}` : ''}${a.floormap_id ? ' (配置済)' : ''}`,
                  }))}
                />
              </div>
              {placingAssetId ? (
                <span className="text-xs font-semibold text-teal-700">マップ上をクリックして配置してください</span>
              ) : (
                <span className="text-[11px] text-zinc-400">資産を選ぶ → マップをクリック</span>
              )}
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-zinc-600">アイコン:</span>
                <Select value={placingIcon} onValueChange={setPlacingIcon}>
                  <SelectTrigger className="h-8 w-48 text-xs bg-white" data-testid="it-pin-icon-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動（カテゴリ連動）</SelectItem>
                    {Object.entries(PIN_ICONS).map(([key, { Icon, label }]) => (
                      <SelectItem key={key} value={key}>
                        <span className="inline-flex items-center gap-1.5"><Icon size={13} /> {label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant={placingRack ? 'default' : 'outline'} size="sm"
                className={`h-8 text-xs shrink-0 ${placingRack ? 'bg-zinc-800 hover:bg-zinc-700' : ''}`}
                onClick={() => setPlacingRack((v) => !v)} data-testid="it-rack-place-btn">
                <Rows size={13} className="mr-1" /> {placingRack ? '配置先をクリック…' : 'ラックを配置'}
              </Button>
            </div>
          )}

          {connecting && (
            <div className="flex items-center gap-2 flex-wrap rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="it-connect-banner">
              <Plugs size={15} weight="duotone" className="text-amber-600 shrink-0" />
              <span className="text-xs font-semibold text-amber-800">
                「{pinById.get(connecting.fromId)?.name || '資産'}」から {CABLE_TYPES[connecting.cableType]?.label} を接続 — 接続先のピンをクリックしてください
              </span>
              <Input value={connectLabel} onChange={(e) => setConnectLabel(e.target.value)}
                placeholder="メモ: ポート/VLAN等（任意）" className="h-7 w-52 text-xs bg-white"
                data-testid="it-connect-label-input" />
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-zinc-500 ml-auto" onClick={() => { setConnecting(null); setConnectLabel(''); }} data-testid="it-connect-cancel">
                キャンセル
              </Button>
            </div>
          )}

          {/* 印刷用表紙（全マップ一括印刷の最初の1枚） */}
          {printCoverMode && coverStats && (
            <div id="floormap-print-cover" data-testid="floormap-print-cover">
              <div className="cover-hero">
                <div className="cover-brand">Sensbey</div>
                <div className="cover-title">IT フロアマップ 現場引継ぎパック</div>
                <div className="cover-date">出力日: {coverStats.date}</div>
              </div>
              <div className="cover-summary">
                <div className="cover-stat"><span className="n">{coverStats.maps}</span><span className="lbl">フロアマップ</span></div>
                <div className="cover-stat"><span className="n">{coverStats.assets}</span><span className="lbl">配置済み資産</span></div>
                <div className="cover-stat"><span className="n">{coverStats.racks}</span><span className="lbl">サーバーラック</span></div>
                <div className="cover-stat"><span className="n">{coverStats.conns}</span><span className="lbl">ケーブル接続</span></div>
              </div>
              <div className="cover-list-title">収録マップ</div>
              <table className="cover-list">
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>#</th>
                    <th>マップ名</th>
                    <th style={{ width: '14%' }} className="num">配置資産</th>
                    <th style={{ width: '12%' }} className="num">ラック</th>
                    <th style={{ width: '14%' }} className="num">ケーブル</th>
                  </tr>
                </thead>
                <tbody>
                  {coverStats.perMap.map((r, i) => (
                    <tr key={r.map.id}>
                      <td className="num">{i + 1}</td>
                      <td>{r.map.name}</td>
                      <td className="num">{r.pinned + r.racked}</td>
                      <td className="num">{r.racks}</td>
                      <td className="num">{r.conns}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cover-legend">
                <span className="ttl">ケーブル凡例</span>
                <span><i style={{ background: '#e11d48' }} />光ファイバー</span>
                <span><i style={{ background: '#059669' }} />CAT6E</span>
                <span><i style={{ background: '#ca8a04' }} />CAT6</span>
                <span><i style={{ background: '#0284c7' }} />CAT5</span>
                <span><i style={{ background: '#7c3aed' }} />無線Wi-Fi</span>
              </div>
              <div className="cover-footer">機密情報 — 施設現場担当者以外への配布を禁じます</div>
            </div>
          )}

          {selectedMap && (
            <div id="floormap-print-root" className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
              {/* 印刷時のみ表示されるヘッダー */}
              <div id="it-floormap-print-header" data-testid="it-floormap-print-header" style={{ display: 'none' }} className="lg:col-span-2">
                <div>
                  <div className="fm-print-title">{selectedMap.name}</div>
                  <div className="fm-print-meta">フロアマップ／{formatDate(new Date())} 出力・グループ {groups.length}件・接続 {conns.length}本</div>
                </div>
                <div className="fm-print-legend">
                  <span><i style={{ background: '#e11d48' }} /> 光ファイバー</span>
                  <span><i style={{ background: '#ca8a04' }} /> CAT6</span>
                  <span><i style={{ background: '#059669' }} /> CAT6E</span>
                  <span><i style={{ background: '#0284c7' }} /> CAT5</span>
                  <span><i style={{ background: '#7c3aed' }} /> 無線Wi-Fi</span>
                </div>
              </div>
              <div className="space-y-2">
                <div ref={viewportRef}
                  className={`relative rounded-xl border border-zinc-200 overflow-hidden bg-zinc-50 select-none ${
                    placingAssetId || connecting ? 'cursor-crosshair ring-2 ring-blue-400' : zoom > 1 ? 'cursor-grab' : ''
                  }`}
                  onMouseDown={onPointerDown} onMouseMove={onPointerMove}
                  onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
                  data-testid="it-floormap-canvas">
                  <div style={{ transform: `translate(${viewT.x}px, ${viewT.y}px) scale(${zoom})`, transformOrigin: '0 0' }} className="relative">
                    <img ref={imgRef} src={fileUrl(selectedMap.image_url)} alt={selectedMap.name}
                      className="w-full select-none"
                      onLoad={() => setImgSize({ w: imgRef.current?.offsetWidth || 0, h: imgRef.current?.offsetHeight || 0 })}
                      onClick={onMapClick} draggable={false} />

                    {/* グループカード — 使用者/共有部門ごとの資産一覧（ドラッグ移動・右下でリサイズ） */}
                    {groups.length > 0 && imgSize.w > 0 && groups.map((g) => {
                      const r = groupRects.get(g.key);
                      if (!r) return null;
                      const dimmedCard = viewMode === 'network' && !g.members.some((m) => NETWORK_KEYS.has(iconKeyFor(m)));
                      return (
                        <div key={g.key}
                          ref={(el) => { if (el) cardEls.current.set(g.key, el); else cardEls.current.delete(g.key); }}
                          className={`absolute z-10 select-none transition-opacity ${dimmedCard ? 'opacity-25' : ''} ${canEdit && !connecting ? 'cursor-move' : ''}`}
                          style={{
                            left: `${r.x}%`, top: `${r.y}%`,
                            width: r.wSet ? `${r.w}%` : undefined,
                            height: r.hSet ? `${r.h}%` : undefined,
                            minWidth: 118,
                          }}
                          onMouseDown={(e) => onGroupMouseDown(e, g)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`it-group-card-${g.key}`}>
                          <div className={`relative w-full h-full flex flex-col bg-white/95 border border-indigo-900/50 shadow-md overflow-hidden ${connecting ? 'ring-2 ring-amber-400' : ''}`}>
                            {/* 角ブラケット（技術図面スタイル） */}
                            <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-indigo-900 pointer-events-none z-10" />
                            <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-indigo-900 pointer-events-none z-10" />
                            <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-indigo-900 pointer-events-none z-10" />
                            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-indigo-900 pointer-events-none z-10" />
                            {/* ヘッダー: 使用者名 + ケーブル種別カウント + 台数 */}
                            <div className="flex items-center gap-1 px-2 py-1 border-b border-indigo-900/30 bg-indigo-50/90 shrink-0"
                              data-testid={`it-group-card-header-${g.key}`}>
                              {g.key.startsWith('seat:')
                                ? <MapPin size={10} weight="fill" className="text-indigo-900 shrink-0" />
                                : <User size={10} weight="fill" className="text-indigo-900 shrink-0" />}
                              <span className="text-[9px] font-bold tracking-wide text-indigo-950 truncate">{g.label}</span>
                              {(() => {
                                const ports = groupPorts.byGroup.get(g.key) || [];
                                if (ports.length === 0) return null;
                                const counts = new Map();
                                ports.forEach((p) => {
                                  // legacy 'lan' は新規 'cat6' として集計 — 表示重複を避ける
                                  let key = p.cableType === 'lan' ? 'cat6' : p.cableType;
                                  if (!CABLE_TYPES[key]) key = 'other';
                                  counts.set(key, (counts.get(key) || 0) + 1);
                                });
                                const order = ['fiber', 'cat6e', 'cat6', 'cat5', 'wifi', 'other'];
                                return (
                                  <span className="ml-auto inline-flex items-center gap-0.5 shrink-0"
                                    data-testid={`it-group-cable-chips-${g.key}`}>
                                    {order.filter((k) => counts.has(k)).map((k) => {
                                      const ct = CABLE_TYPES[k];
                                      return (
                                        <span key={k} title={`${ct.label} ${counts.get(k)}本`}
                                          className="inline-flex items-center gap-[2px] rounded-full bg-white/90 border border-zinc-200 px-1 py-[1px] text-[8px] font-bold tabular-nums leading-none"
                                          style={{ color: ct.color }}
                                          data-testid={`it-group-cable-chip-${g.key}-${k}`}>
                                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ct.color }} />
                                          {ct.short}{counts.get(k)}
                                        </span>
                                      );
                                    })}
                                  </span>
                                );
                              })()}
                              <span className={`text-[8px] font-bold text-indigo-900/70 tabular-nums shrink-0 ${(groupPorts.byGroup.get(g.key) || []).length > 0 ? '' : 'ml-auto'}`}>{g.members.length}台</span>
                            </div>
                            {/* 資産行: タグ | 品名 */}
                            <div className="flex-1 min-h-0 overflow-hidden divide-y divide-zinc-200">
                              {g.members.map((a) => {
                                const rowKey = iconKeyFor(a);
                                const { Icon: RowIcon } = PIN_ICONS[rowKey];
                                const col = categoryColor(a.category);
                                return (
                                  <button key={a.id} type="button"
                                    className="w-full flex items-center gap-1.5 px-2 py-[3px] text-left hover:bg-blue-50/80 transition-colors"
                                    onClick={(e) => onGroupRowClick(e, a)}
                                    title={`${a.name}${a.category ? `（${a.category}）` : ''}`}
                                    data-testid={`it-group-row-${a.id}`}>
                                    <span className="flex items-center justify-center w-3.5 h-3.5 rounded-sm text-white shrink-0" style={{ backgroundColor: col.dot }}>
                                      <RowIcon size={9} weight="fill" />
                                    </span>
                                    {a.asset_tag && <span className="text-[8px] font-mono font-bold text-zinc-700 shrink-0">{a.asset_tag}</span>}
                                    <span className="text-[8px] text-zinc-500 truncate">{a.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {/* リサイズハンドル（右下） */}
                            {canEdit && !connecting && (
                              <span onMouseDown={(e) => onGroupResizeDown(e, g)}
                                title="ドラッグでサイズ変更"
                                className="absolute bottom-0 right-0 w-3.5 h-3.5 cursor-nwse-resize z-20"
                                style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(30,58,138,0.55) 50%)' }}
                                data-testid={`it-group-resize-${g.key}`} />
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* カードのケーブルポート — 接続ごとの色付きドット（ホバーで配線をハイライト） */}
                    {showCables && imgSize.w > 0 && [...groupPorts.byGroup.entries()].map(([gKey, ports]) => ports.map((p) => {
                      const ct = CABLE_TYPES[p.cableType] || CABLE_TYPES.other;
                      const hovered = hoverConn === p.id;
                      return (
                        <span key={`${gKey}-${p.id}-${p.end}`}
                          className="absolute z-20 rounded-full ring-1 ring-white shadow-sm cursor-pointer"
                          style={{
                            left: `${p.x}%`, top: `${p.y}%`, width: 8, height: 8,
                            marginLeft: -4, marginTop: -4, backgroundColor: ct.color,
                            transform: hovered ? 'scale(1.6)' : 'scale(1)',
                            transition: 'transform 0.15s ease',
                          }}
                          onMouseEnter={() => setHoverConn(p.id)}
                          onMouseLeave={() => setHoverConn(null)}
                          title={ct.label}
                          data-testid={`it-group-port-${p.id}-${p.end}`} />
                      );
                    }))}

                    {/* ケーブル配線オーバーレイ — 直角ルーティング */}
                    {showCables && imgSize.w > 0 && (
                      <svg className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox={`0 0 ${imgSize.w} ${imgSize.h}`} preserveAspectRatio="none"
                        data-testid="it-floormap-cables">
                        {visibleConns.map((c) => {
                          const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
                          const hovered = hoverConn === c.id;
                          return (
                            <g key={c.id}>
                              <path d={cablePath(c)} fill="none"
                                stroke={ct.color} strokeWidth={(hovered ? 4 : 2.5) * invScale}
                                strokeLinecap="round" strokeLinejoin="round"
                                opacity={hoverConn && !hovered ? 0.35 : 1}
                                strokeDasharray={ct.dash.length ? ct.dash.map((n) => n * invScale).join(' ') : undefined}
                                data-testid={`it-cable-${c.id}`} />
                              <path d={cablePath(c)} fill="none" stroke="transparent"
                                strokeWidth={14 * invScale} strokeLinecap="round" strokeLinejoin="round"
                                style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                                onMouseEnter={() => setHoverConn(c.id)}
                                onMouseLeave={() => setHoverConn(null)}
                                data-testid={`it-cable-hit-${c.id}`} />
                            </g>
                          );
                        })}
                      </svg>
                    )}

                    {/* ケーブルホバーツールチップ — ポート/VLANメモ表示 */}
                    {showCables && hoverConn && (() => {
                      const c = visibleConns.find((x) => x.id === hoverConn);
                      if (!c) return null;
                      const f = assetById.get(c.from_asset_id);
                      const t = assetById.get(c.to_asset_id);
                      const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
                      const fc = groupPorts.byConn.get(`${c.id}:from`) || effPosById.get(c.from_asset_id);
                      const tc = groupPorts.byConn.get(`${c.id}:to`) || effPosById.get(c.to_asset_id);
                      return (
                        <div className="absolute z-20 pointer-events-none"
                          style={{
                            left: `${(fc.x + tc.x) / 2}%`, top: `${(fc.y + tc.y) / 2}%`,
                            transform: `translate(-50%, -120%) scale(${invScale})`, transformOrigin: 'bottom center',
                          }}
                          data-testid="it-cable-tooltip">
                          <div className="rounded-lg bg-zinc-900/95 text-white px-2.5 py-1.5 shadow-xl whitespace-nowrap">
                            <p className="text-[10px] font-bold">{f.name} — {t.name}</p>
                            <p className="text-[9px] flex items-center gap-1 mt-0.5" style={{ color: ct.color === '#71717a' ? '#d4d4d8' : ct.color }}>
                              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: ct.color }} />{ct.label}
                            </p>
                            {c.label && <p className="text-[9px] text-zinc-300 mt-0.5 font-mono" data-testid="it-cable-tooltip-label">{c.label}</p>}
                          </div>
                        </div>
                      );
                    })()}

                    {pinned.filter((a) => !groupKeyByAsset.has(a.id)).map((a) => {
                      const key = iconKeyFor(a);
                      const { Icon } = PIN_ICONS[key];
                      const isPin = key === 'pin';
                      const dimmed = viewMode === 'network' && !NETWORK_KEYS.has(key);
                      const pos = coordsFor(a);
                      const ws = walkStatusById.get(a.id);
                      const catCol = categoryColor(a.category);
                      // walk & connecting state overrides category tint
                      const stateOverride = ws === 'ok' || ws === 'missing' || !!connecting;
                      const pinColor = ws === 'ok' ? 'text-emerald-500'
                        : ws === 'missing' ? 'text-rose-500 animate-pulse'
                          : connecting ? 'text-amber-500' : '';
                      const badgeColor = ws === 'ok' ? 'bg-emerald-500'
                        : ws === 'missing' ? 'bg-rose-500 animate-pulse'
                          : connecting ? 'bg-amber-500' : '';
                      const pinStyle = !stateOverride ? { color: catCol.dot } : undefined;
                      const badgeStyle = !stateOverride ? { backgroundColor: catCol.dot } : undefined;
                      return (
                        <button key={a.id} type="button"
                          className={`absolute group transition-opacity ${dimmed ? 'opacity-25' : ''} ${canEdit && !connecting ? 'cursor-move' : ''}`}
                          style={{
                            left: `${pos.x}%`, top: `${pos.y}%`,
                            transform: isPin ? `translate(-50%, -100%) scale(${invScale})` : `translate(-50%, -50%) scale(${invScale})`,
                            transformOrigin: isPin ? '50% 100%' : '50% 50%',
                          }}
                          onMouseDown={(e) => onPinMouseDown(e, a)}
                          onClick={(e) => onPinClick(e, a)}
                          title={`${a.name}${a.category ? `（${a.category}）` : ''}`}
                          data-testid={`it-pin-${a.id}`}>
                          {isPin ? (
                            <MapPin size={26} weight="fill" className={`drop-shadow-md transition-colors ${pinColor}`} style={pinStyle} />
                          ) : (
                            <span className={`flex items-center justify-center w-7 h-7 rounded-full text-white shadow-md ring-2 ring-white transition-colors ${badgeColor}`} style={badgeStyle}>
                              <Icon size={15} weight="fill" />
                            </span>
                          )}
                          <span className="absolute left-1/2 -translate-x-1/2 -top-6 whitespace-nowrap rounded-md bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            {a.name}
                          </span>
                        </button>
                      );
                    })}

                    {/* ラックマーカー */}
                    {racks.map((r) => {
                      const cnt = rackedAssets.filter((a) => a.rack_id === r.id).length;
                      const { used, free, ratio } = usageFor(r);
                      const pos = dragRack?.id === r.id ? dragRack : { x: r.pin_x, y: r.pin_y };
                      const dimmedRack = viewMode === 'network' && cnt === 0;
                      return (
                        <button key={r.id} type="button"
                          className={`absolute group transition-opacity ${dimmedRack ? 'opacity-25' : ''} ${canEdit && !connecting ? 'cursor-move' : ''}`}
                          style={{
                            left: `${pos.x}%`, top: `${pos.y}%`,
                            transform: `translate(-50%, -50%) scale(${invScale})`, transformOrigin: '50% 50%',
                          }}
                          onMouseDown={(e) => onRackMouseDown(e, r)}
                          onClick={(e) => onRackClick(e, r)}
                          title={`${r.name}（使用${used}U / 空き${free}U・${cnt}台）`}
                          data-testid={`it-rack-marker-${r.id}`}>
                          <span className={`relative flex items-center justify-center w-8 h-8 rounded-md text-white shadow-md ring-2 ring-white transition-colors ${
                            connecting ? 'bg-amber-600' : 'bg-zinc-800 group-hover:bg-zinc-700'
                          }`}>
                            <Rows size={16} weight="fill" />
                            {cnt > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-0.5 rounded-full bg-teal-700 text-white text-[9px] font-bold flex items-center justify-center ring-1 ring-white">
                                {cnt}
                              </span>
                            )}
                          </span>
                          <span className={`absolute left-1/2 -translate-x-1/2 -bottom-3.5 whitespace-nowrap rounded-full text-white text-[8px] font-bold font-mono px-1.5 py-px ring-1 ring-white shadow-sm leading-tight ${usageCls(ratio)}`}
                            data-testid={`it-rack-usage-${r.id}`}>
                            {used}/{r.units}U
                          </span>
                          <span className="absolute left-1/2 -translate-x-1/2 -top-6 whitespace-nowrap rounded-md bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            {r.name}（使用{used}U・空き{free}U）
                          </span>
                        </button>
                      );
                    })}

                    {/* ピン編集ポップオーバー — アイコン変更・接続・詳細・解除 */}
                    {popoverAsset && (() => {
                      const pp = effPosById.get(popoverAsset.id) || { x: popoverAsset.pin_x, y: popoverAsset.pin_y };
                      return (
                      <div className="absolute z-20"
                        style={{ left: `${pp.x}%`, top: `${pp.y}%`, transform: `translate(-50%, 14px) scale(${invScale})`, transformOrigin: 'top center' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        data-testid="it-pin-popover">
                        <div className="w-64 rounded-xl border border-zinc-200 bg-white shadow-xl p-2.5 space-y-2 cursor-default">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-zinc-800 truncate">{popoverAsset.name}</p>
                              {popoverAsset.asset_tag && <p className="text-[10px] font-mono text-zinc-400">{popoverAsset.asset_tag}</p>}
                            </div>
                            <button type="button" className="text-zinc-400 hover:text-zinc-600 shrink-0" onClick={() => setPopoverId(null)} data-testid="it-pin-popover-close">
                              <X size={13} />
                            </button>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-zinc-500 mb-1">アイコンを変更</p>
                            <div className="grid grid-cols-6 gap-1">
                              <button type="button" onClick={() => changeIcon(popoverAsset.id, null)}
                                title="自動（カテゴリ連動）"
                                data-testid="it-pin-popover-icon-auto"
                                className={`flex items-center justify-center h-8 rounded-md border text-[9px] font-semibold transition-colors ${
                                  !popoverAsset.pin_icon ? 'bg-teal-700 text-white border-teal-700' : 'bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-100'
                                }`}>
                                自動
                              </button>
                              {Object.entries(PIN_ICONS).map(([key, { Icon, label }]) => (
                                <button key={key} type="button" onClick={() => changeIcon(popoverAsset.id, key)}
                                  title={label}
                                  data-testid={`it-pin-popover-icon-${key}`}
                                  className={`flex items-center justify-center h-8 rounded-md border transition-colors ${
                                    popoverAsset.pin_icon === key ? 'bg-teal-700 text-white border-teal-700' : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                                  }`}>
                                  <Icon size={15} />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-zinc-500 mb-1">席/グループ名 <span className="font-normal text-zinc-400">— 同じ名前の資産が1枚のカードにまとまります</span></p>
                            <div className="flex items-center gap-1">
                              <Input value={groupNameDraft} onChange={(e) => setGroupNameDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') savePinGroup(); }}
                                placeholder="例: 受付PC" maxLength={40} className="h-7 text-[11px] flex-1"
                                data-testid="it-pin-group-input" />
                              <Button size="sm" className="h-7 px-2 text-[10px] bg-teal-700 hover:bg-teal-800"
                                onClick={savePinGroup}
                                disabled={(groupNameDraft.trim() || '') === (popoverAsset.pin_group || '')}
                                data-testid="it-pin-group-save">
                                保存
                              </Button>
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-zinc-500 mb-1 flex items-center gap-1"><Plugs size={11} weight="bold" /> ケーブル接続</p>
                            {assetConns.length > 0 && (
                              <div className="space-y-0.5 mb-1">
                                {assetConns.map((c) => {
                                  const otherId = c.from_asset_id === popoverAsset.id ? c.to_asset_id : c.from_asset_id;
                                  const other = assetById.get(otherId);
                                  const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
                                  return (
                                    <div key={c.id} className="flex items-center gap-1.5 text-[10px] text-zinc-600">
                                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ct.color }} />
                                      <span className="truncate flex-1">{other?.name || '?'}<span className="text-zinc-400 ml-1">{ct.label}</span></span>
                                      <button type="button" className="text-zinc-400 hover:text-rose-500" onClick={() => deleteConnection(c.id)} data-testid={`it-conn-delete-${c.id}`}>
                                        <X size={11} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-1">
                              {CABLE_TYPE_KEYS.map((key) => {
                                const ct = CABLE_TYPES[key];
                                return (
                                  <button key={key} type="button" onClick={() => startConnect(popoverAsset.id, key)}
                                    data-testid={`it-pin-popover-connect-${key}`}
                                    title={ct.label}
                                    className="inline-flex items-center justify-center gap-1 h-7 rounded-md border border-zinc-200 bg-zinc-50 text-[10px] font-semibold text-zinc-600 hover:bg-zinc-100 transition-colors">
                                    <span className="w-2 h-2 rounded-full" style={{ background: ct.color }} />
                                    {ct.short}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-[9px] text-zinc-400 mt-0.5">種別を選ぶ → 接続先のピンをクリック</p>
                          </div>
                          <div className="flex items-center justify-between gap-2 pt-0.5">
                            <button type="button" onClick={() => navigate(`/it/assets?open=${popoverAsset.id}`)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-blue-700"
                              data-testid="it-pin-popover-detail">
                              <ArrowSquareOut size={12} weight="bold" /> 詳細を見る
                            </button>
                            <button type="button" onClick={() => removePin(popoverAsset.id)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-500 hover:text-rose-600"
                              data-testid="it-pin-popover-remove">
                              <Trash size={12} weight="bold" /> ピン解除
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })()}
                  </div>

                  {/* ズームコントロール */}
                  <div className="absolute right-2 top-2 z-30 flex flex-col items-center gap-1">
                    <button type="button" onClick={() => zoomBy(1.4)} title="拡大"
                      className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/95 border border-zinc-200 shadow-sm text-zinc-600 hover:text-teal-700 hover:border-blue-200 transition-colors"
                      data-testid="it-floormap-zoom-in">
                      <MagnifyingGlassPlus size={16} weight="bold" />
                    </button>
                    <button type="button" onClick={() => zoomBy(1 / 1.4)} title="縮小" disabled={zoom <= 1}
                      className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/95 border border-zinc-200 shadow-sm text-zinc-600 hover:text-teal-700 hover:border-blue-200 transition-colors disabled:opacity-40"
                      data-testid="it-floormap-zoom-out">
                      <MagnifyingGlassMinus size={16} weight="bold" />
                    </button>
                    {zoom > 1 && (
                      <button type="button" onClick={resetView} title="全体表示に戻す"
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/95 border border-zinc-200 shadow-sm text-zinc-600 hover:text-teal-700 hover:border-blue-200 transition-colors"
                        data-testid="it-floormap-zoom-reset">
                        <ArrowsIn size={16} weight="bold" />
                      </button>
                    )}
                    {zoom > 1 && (
                      <span className="px-1.5 py-0.5 rounded bg-white/95 border border-zinc-200 text-[10px] font-semibold text-zinc-500 tabular-nums" data-testid="it-floormap-zoom-level">
                        {Math.round(zoom * 100)}%
                      </span>
                    )}
                  </div>
                </div>

                {/* 凡例 — 使用中のカテゴリ + ケーブル種別 */}
                {(pinned.length > 0 || visibleConns.length > 0) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1" data-testid="it-floormap-legend">
                    {[...new Set(pinned.map((a) => a.category || '未分類'))].map((cat) => {
                      const key = iconKeyFor({ category: cat === '未分類' ? '' : cat });
                      const { Icon } = PIN_ICONS[key];
                      const col = categoryColor(cat === '未分類' ? '' : cat);
                      return (
                        <span key={cat} className="inline-flex items-center gap-1 text-[10px] text-zinc-500" data-testid={`it-legend-cat-${cat}`}>
                          <span className="flex items-center justify-center w-4 h-4 rounded-full text-white" style={{ backgroundColor: col.dot }}><Icon size={10} weight="fill" /></span>
                          {cat}
                        </span>
                      );
                    })}
                    {showCables && [...new Set(visibleConns.map((c) => (CABLE_TYPES[c.cable_type] ? c.cable_type : 'other')))].map((key) => {
                      const ct = CABLE_TYPES[key];
                      return (
                        <span key={`cable-${key}`} className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
                          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke={ct.color} strokeWidth="2.5" strokeDasharray={ct.dash.length ? ct.dash.map((n) => n / 2).join(' ') : undefined} /></svg>
                          {ct.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {walk && (
                  <WalkPanel walk={walk} setWalk={setWalk} pinned={[...pinned, ...rackedAssets]}
                    onFinished={onWalkFinished} onCancel={() => setWalk(null)} />
                )}
                {walkResult && !walkResultOpen && (
                  <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px]" data-testid="it-walk-result-bar">
                    <span className="font-semibold text-zinc-600 inline-flex items-center gap-1">
                      <ClipboardText size={13} weight="duotone" className="text-emerald-600" /> 棚卸結果
                      {(walkResult.summary?.missing_asset_ids?.length || 0) > 0 && (
                        <span className="text-rose-500">未確認 {walkResult.summary.missing_asset_ids.length}件</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2">
                      <button type="button" className="text-teal-700 font-semibold" onClick={() => setWalkResultOpen(true)} data-testid="it-walk-result-show">表示</button>
                      <button type="button" className="text-zinc-400" onClick={() => setWalkResult(null)} data-testid="it-walk-result-clear">クリア</button>
                    </span>
                  </div>
                )}
                <div className="space-y-2" data-testid="it-pin-list">
                  <div className="text-xs font-semibold text-zinc-600">このマップの資産 ({pinned.length})</div>
                  {pinned.length === 0 ? (
                    <div className="text-[11px] text-zinc-400">まだピンがありません</div>
                  ) : (
                    pinned.map((a) => {
                      const key = iconKeyFor(a);
                      const { Icon } = PIN_ICONS[key];
                      const dimmed = viewMode === 'network' && !NETWORK_KEYS.has(key);
                      const catCol = categoryColor(a.category);
                      return (
                        <div key={a.id} className={`flex items-center justify-between rounded-lg border border-zinc-100 px-2.5 py-1.5 bg-white transition-opacity ${dimmed ? 'opacity-40' : ''}`}>
                          <button type="button" className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-teal-700 truncate text-left"
                            onClick={() => (canEdit ? setPopoverId(a.id) : navigate(`/it/assets?open=${a.id}`))}
                            data-testid={`it-pin-list-item-${a.id}`}>
                            <Icon size={14} weight="duotone" className="shrink-0" style={{ color: catCol.dot }} />
                            <span className="min-w-0 truncate">
                              {a.name}
                              {a.asset_tag && <span className="block text-[10px] font-mono font-normal text-zinc-400">{a.asset_tag}</span>}
                            </span>
                          </button>
                          {canEdit && (
                            <button type="button" className="text-zinc-400 hover:text-rose-500 shrink-0 ml-1"
                              title="ピンを解除" onClick={() => removePin(a.id)}
                              data-testid={`it-pin-remove-${a.id}`}>
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {racks.length > 0 && (
                  <div className="space-y-2" data-testid="it-rack-list">
                    <div className="text-xs font-semibold text-zinc-600 flex items-center gap-1">
                      <Rows size={13} weight="duotone" className="text-zinc-700" /> ラック ({racks.length})
                    </div>
                    {racks.map((r) => {
                      const cnt = rackedAssets.filter((a) => a.rack_id === r.id).length;
                      const { used, free, ratio } = usageFor(r);
                      return (
                        <button key={r.id} type="button"
                          className="w-full rounded-lg border border-zinc-100 px-2.5 py-1.5 bg-white text-left hover:border-zinc-300 transition-colors"
                          onClick={() => setRackDialogId(r.id)}
                          data-testid={`it-rack-list-item-${r.id}`}>
                          <span className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-zinc-700 truncate">{r.name}</span>
                            <span className="text-[10px] font-mono text-zinc-400 shrink-0 ml-2">{used}/{r.units}U・{cnt}台</span>
                          </span>
                          <span className="mt-1 flex items-center gap-1.5">
                            <span className="flex-1 h-1 rounded-full bg-zinc-100 overflow-hidden">
                              <span className={`block h-full rounded-full ${usageCls(ratio)}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
                            </span>
                            <span className="text-[9px] font-mono text-zinc-400 shrink-0" data-testid={`it-rack-list-free-${r.id}`}>空き{free}U</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {showCables && (
                  <div className="space-y-2" data-testid="it-conn-list">
                    <div className="text-xs font-semibold text-zinc-600 flex items-center gap-1"><Plugs size={13} weight="duotone" className="text-amber-600" /> ケーブル接続 ({visibleConns.length})</div>
                    {visibleConns.length === 0 ? (
                      <div className="text-[11px] text-zinc-400">接続がありません{canEdit ? ' — ピンをクリック → 種別を選択で追加できます' : ''}</div>
                    ) : (
                      visibleConns.map((c) => {
                        const f = assetById.get(c.from_asset_id);
                        const t = assetById.get(c.to_asset_id);
                        const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
                        return (
                          <div key={c.id} className="flex items-center justify-between rounded-lg border border-zinc-100 px-2.5 py-1.5 bg-white" data-testid={`it-conn-row-${c.id}`}>
                          <div className="min-w-0 flex-1 text-[11px] text-zinc-700">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ct.color }} />
                              <span className="font-semibold truncate">{f?.name}</span>
                            </span>
                            <span className="mx-1 text-zinc-400">—</span>
                            <span className="font-semibold truncate">{t?.name}</span>
                            <span className="block text-[10px] text-zinc-400">
                              {ct.label}
                              {c.source === 'omada' && (
                                <span className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[9px] font-semibold align-middle" data-testid={`it-conn-auto-${c.id}`}>
                                  <Lightning size={8} weight="fill" /> 自動
                                </span>
                              )}
                              {c.label && <span className="ml-1.5 font-mono text-zinc-500" data-testid={`it-conn-label-${c.id}`}>{c.label}</span>}
                            </span>
                            {editingConn?.id === c.id && (
                              <div className="flex gap-1 mt-1">
                                <Input value={editingConn.label}
                                  onChange={(e) => setEditingConn({ ...editingConn, label: e.target.value })}
                                  onKeyDown={(e) => e.key === 'Enter' && saveConnLabel()}
                                  placeholder="ポート/VLAN等のメモ" className="h-6 text-[10px]" autoFocus
                                  data-testid={`it-conn-label-input-${c.id}`} />
                                <Button size="sm" className="h-6 px-2 text-[10px] bg-teal-700 hover:bg-teal-800" onClick={saveConnLabel} data-testid={`it-conn-label-save-${c.id}`}>保存</Button>
                              </div>
                            )}
                          </div>
                          {canEdit && (
                            <span className="flex items-center shrink-0 ml-1 gap-0.5">
                              <button type="button" className="text-zinc-400 hover:text-teal-700"
                                title="メモを編集（ポート/VLAN）"
                                onClick={() => setEditingConn(editingConn?.id === c.id ? null : { id: c.id, label: c.label || '' })}
                                data-testid={`it-conn-edit-${c.id}`}>
                                <PencilSimple size={12} />
                              </button>
                              <button type="button" className="text-zinc-400 hover:text-rose-500"
                                title="接続を削除" onClick={() => deleteConnection(c.id)}
                                data-testid={`it-conn-list-delete-${c.id}`}>
                                <X size={13} />
                              </button>
                            </span>
                          )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {canEdit && unmatched && (unmatched.devices.length > 0 || unmatched.clients.length > 0) && (
                  <UnmatchedOmadaList
                    unmatched={unmatched}
                    onRegister={(row) => openQuickReg(row, !!row.type)}
                    onLink={(row) => setLinkTarget(row)}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ラックビュー / 作成 / 接続先機器選択 */}
      <RackDialog open={!!rackDialogId} rack={rackById.get(rackDialogId)}
        assets={rackedAssets} allAssets={assets} canEdit={canEdit} conns={conns}
        onClose={() => setRackDialogId(null)}
        onChanged={() => { load(); loadRacks(); loadConns(); }}
        onStartConnect={(aid, type) => setConnecting({ fromId: aid, cableType: type })} />

      {/* Omada未登録機器 → クイック資産登録 */}
      <AssetFormDialog open={!!quickReg} onClose={() => setQuickReg(null)}
        prefill={quickReg?.prefill} prefillIp={quickReg?.prefillIp}
        onSaved={() => { load(); loadUnmatched(); }} />

      {/* Omada未登録機器 → 既存資産に紐付け（および +登録 時の類似候補提示） */}
      <LinkOmadaDialog open={!!linkTarget} target={linkTarget}
        suggestionsOnly={!!linkTarget?.__fallbackRegister}
        onClose={() => setLinkTarget(null)}
        onLinked={() => { load(); loadUnmatched(); itApi.assets().then(setAssetsCache).catch(() => {}); }}
        onCreateNew={linkTarget?.__fallbackRegister
          ? () => { const { row, isDevice } = linkTarget.__fallbackRegister; setQuickReg(buildQuickReg(row, isDevice)); }
          : null} />

      <Dialog open={!!newRack} onOpenChange={(v) => !v && setNewRack(null)}>
        <DialogContent className="max-w-xs" data-testid="it-rack-create-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Rows size={16} weight="duotone" /> ラックを配置</DialogTitle></DialogHeader>
          {newRack && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">ラック名 *</Label>
                <Input value={newRack.name} onChange={(e) => setNewRack({ ...newRack, name: e.target.value })}
                  placeholder="例: サーバーラックA / 分電盤1" className="h-9 mt-1" autoFocus data-testid="it-rack-create-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">高さ（U数）</Label>
                <Input type="number" min={1} max={200} value={newRack.units}
                  onChange={(e) => setNewRack({ ...newRack, units: e.target.value })}
                  className="h-9 mt-1 w-24" data-testid="it-rack-create-units" />
                <p className="text-[10px] text-zinc-400 mt-1">例: サーバーラック 42U / 壁掛けキャビネット 12U / 引き出し 4U</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setNewRack(null)}>キャンセル</Button>
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={createRack} data-testid="it-rack-create-submit">配置する</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rackPicker} onOpenChange={(v) => !v && setRackPicker(null)}>
        <DialogContent className="max-w-xs" data-testid="it-rack-picker-dialog">
          <DialogHeader><DialogTitle className="text-base">接続先の機器を選択 — {rackPicker?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {rackedAssets.filter((a) => a.rack_id === rackPicker?.id).map((a) => (
              <button key={a.id} type="button"
                className="w-full flex items-center justify-between rounded-lg border border-zinc-200 px-2.5 py-2 text-left hover:border-teal-700 transition-colors"
                onClick={() => { createConnection(a.id); setRackPicker(null); }}
                data-testid={`it-rack-picker-item-${a.id}`}>
                <span className="text-xs font-semibold text-zinc-700 truncate">{a.name}</span>
                <span className="text-[10px] font-mono text-zinc-400 shrink-0 ml-2">
                  U{a.rack_unit}{(a.rack_size || 1) > 1 ? `–U${a.rack_unit + a.rack_size - 1}` : ''} {a.rack_side === 'rear' ? '背面' : '前面'}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* 棚卸結果ダイアログ */}
      <Dialog open={walkResultOpen} onOpenChange={setWalkResultOpen}>
        <DialogContent className="max-w-md" data-testid="it-walk-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={18} weight="duotone" className="text-emerald-600" /> 棚卸結果 — {walkResult?.floormap_name}
            </DialogTitle>
          </DialogHeader>
          {walkResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 py-2">
                  <p className="text-lg font-bold text-emerald-600 tabular-nums" data-testid="it-walk-sum-ok">{walkResult.summary?.ok ?? 0}</p>
                  <p className="text-[10px] text-emerald-700">確認済み</p>
                </div>
                <div className="rounded-lg bg-rose-50 border border-rose-100 py-2">
                  <p className="text-lg font-bold text-rose-600 tabular-nums" data-testid="it-walk-sum-missing">{walkResult.summary?.missing_asset_ids?.length ?? 0}</p>
                  <p className="text-[10px] text-rose-700">未確認</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 py-2">
                  <p className="text-lg font-bold text-amber-600 tabular-nums" data-testid="it-walk-sum-misplaced">{walkResult.summary?.misplaced ?? 0}</p>
                  <p className="text-[10px] text-amber-700">場所違い</p>
                </div>
              </div>
              {(walkResult.missing_assets || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-rose-600 mb-1">未確認（紛失の可能性）— マップ上で赤く点滅表示</p>
                  <div className="space-y-1 max-h-36 overflow-y-auto" data-testid="it-walk-missing-list">
                    {walkResult.missing_assets.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-md border border-rose-100 bg-rose-50/40 px-2 py-1 text-[11px]">
                        <span className="font-semibold text-zinc-700 truncate">{a.name}</span>
                        <span className="font-mono text-[10px] text-zinc-400 shrink-0 ml-2">{a.asset_tag || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(walkResult.misplaced_scans || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-amber-600 mb-1">場所違い（このマップに配置されていない資産）</p>
                  <div className="space-y-1 max-h-28 overflow-y-auto" data-testid="it-walk-misplaced-list">
                    {walkResult.misplaced_scans.map((s, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md border border-amber-100 bg-amber-50/40 px-2 py-1 text-[11px]">
                        <span className="font-semibold text-zinc-700 truncate"><span data-no-translate>{s.asset_name}</span></span>
                        <span className="text-[10px] text-zinc-400 shrink-0 ml-2">{s.location || '場所未設定'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(walkResult.summary?.unknown ?? 0) > 0 && (
                <p className="text-[11px] text-zinc-500">不明コードのスキャン: {walkResult.summary.unknown}件</p>
              )}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setWalkResultOpen(false)} data-testid="it-walk-result-close">閉じる</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-sm" data-testid="it-floormap-new-dialog">
          <DialogHeader><DialogTitle>フロアマップを追加</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">名前 *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="例: ビル1 フロア2" className="h-10 mt-1" data-testid="it-floormap-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">見取り図画像 (PNG/JPG) *</Label>
              <input type="file" accept="image/*" className="mt-1 block w-full text-xs text-zinc-600 file:mr-2 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-teal-700 hover:file:bg-blue-100"
                onChange={(e) => setNewFile(e.target.files?.[0] || null)} data-testid="it-floormap-file-input" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setNewOpen(false)}>キャンセル</Button>
              <Button onClick={createMap} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="it-floormap-create-btn">追加する</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
